/**
 * Shows a list of users which are registered for the application.
 * Material design: Table from https://material-ui.com/components/tables/#table
 */

import React, {useContext, useEffect, useState} from 'react'
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import {NavLink} from "react-router-dom";
import AppbarActionButton from "../../../../commons/elements/buttons/AppbarActionButton";
import AddIcon from "@mui/icons-material/Add";
import {AppbarContentContext} from "../../../../commons/routing/appbar/AppbarContentProvider";
import {StyledTableCell, StyledTableRow} from "../../../../commons/utils/table/StyledTablePrimary.css";
import useListCourses from "./useListCourses";
import {Box, CircularProgress, Collapse, IconButton} from "@mui/material";
import {Delete, Edit, Info, KeyboardArrowDown, KeyboardArrowUp} from "@mui/icons-material";
import CourseSummary from "../courseSummary/CourseSummary";
import usePagination from "../../../../commons/utils/usePagination";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import TagContainer from "../../../../commons/utils/disciplines/TagContainer";
import CourseCard from "../card/course/CourseCard";
import {FormattedMessage, useIntl} from "react-intl";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import GenericFilter from "../../../../commons/elements/search/genericSearch/GenericFilter";
import {useStyles} from "./CoursesSection.css";


const CoursesSection = () => {
    const classes = useStyles()
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    // Pagination for the table handling
    const {page, rowsPerPage, handleChangePage, handleChangeRowsPerPage} = usePagination()

    // Hook providing the list courses functionality
    const {courses, loading, handleDeleteCourse, deletionInProgress, deletionAttemptedCourse} = useListCourses()

    // State which holds the courses according to the current filter
    const [filteredCourses, setFilteredCourses] = useState([])

    // Collapsible table state
    const [openIndex, setOpenIndex] = React.useState();

    // Internationalization for simple strings
    const intl = useIntl();

    // Checking if the edit/saving operations are allowed
    const {isOperationOnObjectAllowed} = useContext(FirebaseAuthContext);


    // Setting the appbar content
    useEffect(() => {
        setFilteredCourses(courses)

        setCurrentAppbarContent({
            header: intl.formatMessage({id: 'app.courses.overview.appbar.header', defaultMessage: 'Courses'}),
            description: intl.formatMessage({
                id: 'app.courses.overview.appbar.description',
                defaultMessage: 'Manage the courses...'
            }),
            actions: <>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.courses.overview.appbar.actions.new',
                        defaultMessage: 'New Course'
                    })}
                    component={NavLink} to='/admin/courses/new'
                    icon={<AddIcon/>}/>
            </>,
            searchContainer: <GenericFilter objects={courses}
                                            onResultsChanged={(results) => setFilteredCourses(results)}
                                            filterDefinitions={[
                                                {
                                                    type: 'select',
                                                    id: 'app.courses.filter.name',
                                                    options: [...new Set(courses.map((p) => p.name))],
                                                    getOptionLabel: (p) => p,
                                                    condition: (object, filter) => filter?.includes(object.name) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.courses.filter.association',
                                                    options: [...new Set(courses.map((c) => c.association))],
                                                    getOptionLabel: (association) =>
                                                        intl.formatMessage({
                                                            id: `app.ids.association.${association}`,
                                                            defaultMessage: association
                                                        }),
                                                    condition: (object, filter) => filter?.includes(object.association) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.courses.filter.disciplines',
                                                    options: getAllDisciplinesOfCourses(),
                                                    getOptionLabel: (discipline) =>
                                                        intl.formatMessage({
                                                            id: `app.ids.discipline.${discipline}`,
                                                            defaultMessage: discipline
                                                        }),
                                                    condition: (object, filter) => filter.find((fd) => object.disciplines.includes(fd)) || filter?.length === 0
                                                },
                                            ]}/>,
            progress: deletionInProgress
        })
    }, [deletionInProgress, courses])

    const getAllDisciplinesOfCourses = () => {
        if (!courses) {
            return []
        }

        const dList = []
        courses.forEach((c) => {
            dList.push(...c.disciplines)
        })
        return [...new Set(dList)]
    }

    // Show loading indication while loading the list of courses
    if (loading) {
        return <LoadingProgressIndication
            text={intl.formatMessage({id: 'app.courses.overview.loading', defaultMessage: 'Loading Courses...'})}/>
    }

    // Shows the table containing the courses
    return (
        <Paper className={classes.root}>
            <TableContainer className={classes.container}>
                <Table stickyHeader aria-label="sticky table">

                    {/* Table header */}
                    <TableHead>
                        <TableRow>
                            <StyledTableCell align={"center"} width={"5%"} key="collapsible"></StyledTableCell>
                            <StyledTableCell align={"center"} width={"40%"} key="courseCard">
                                <FormattedMessage id={'app.courses.overview.table.column.courseSummary'}
                                                  defaultMessage={'Course Summary'}/>
                            </StyledTableCell>
                            <StyledTableCell align={"center"} width={"20%"}
                                             key='disciplines'>
                                <FormattedMessage id={'app.courses.overview.table.column.disciplines'}
                                                  defaultMessage={'Disciplines'}/>
                            </StyledTableCell>
                            <StyledTableCell align={"center"} width={"10%"} key='fromDate'>
                                <FormattedMessage id={'app.courses.overview.table.column.fromDate'}
                                                  defaultMessage={'From'}/>
                            </StyledTableCell>
                            <StyledTableCell align={"center"} width={"10%"} key='toDate'>
                                <FormattedMessage id={'app.courses.overview.table.column.toDate'}
                                                  defaultMessage={'To'}/>
                            </StyledTableCell>
                            <StyledTableCell align={"right"} width={"15%"} key='actions'>
                                <FormattedMessage id={'app.courses.overview.table.column.actions'}
                                                  defaultMessage={'Actions'}/>
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>

                    {/* Table rows */}
                    <TableBody>
                        {filteredCourses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((courseRow, index) => {
                            return (
                                <React.Fragment key={courseRow.key}>
                                    <StyledTableRow hover key={courseRow.key}>

                                        <StyledTableCell key="collapsible">
                                            {openIndex === index
                                                ? <IconButton aria-label="expand row" size="small"
                                                              onClick={() => setOpenIndex(null)}>
                                                    <KeyboardArrowUp/>
                                                </IconButton>
                                                : <IconButton aria-label="expand row" size="small"
                                                              onClick={() => setOpenIndex(index)}>
                                                    <KeyboardArrowDown/>
                                                </IconButton>}
                                        </StyledTableCell>

                                        <StyledTableCell key='courseCard'>
                                            <CourseCard course={courseRow} textSize={16} headerSize={18}/>
                                        </StyledTableCell>

                                        <StyledTableCell key='disciplines'>
                                            <TagContainer tags={courseRow.disciplines.map((discipline) =>
                                                intl.formatMessage({id: `app.ids.discipline.${discipline}`}))}/>
                                        </StyledTableCell>

                                        <StyledTableCell align={"center"} key='fromDate'>
                                            {intl.formatDate(courseRow.fromDate, {
                                                year: 'numeric',
                                                month: 'numeric',
                                                day: 'numeric',
                                                hour: 'numeric',
                                                minute: 'numeric',
                                            })}
                                        </StyledTableCell>
                                        <StyledTableCell align={"center"} key='toDate'>
                                            {intl.formatDate(courseRow.toDate, {
                                                year: 'numeric',
                                                month: 'numeric',
                                                day: 'numeric',
                                                hour: 'numeric',
                                                minute: 'numeric',
                                            })}
                                        </StyledTableCell>

                                        <StyledTableCell align={"right"} key='actions'>
                                            <IconButton
                                                component={NavLink}
                                                to={'/admin/courses/' + courseRow.key + '/edit'}
                                                size="large">
                                                {isOperationOnObjectAllowed(courseRow)
                                                    ? <Edit/>
                                                    : <Info/>}
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDeleteCourse(courseRow)}
                                                disabled={!isOperationOnObjectAllowed(courseRow)}
                                                size="large">
                                                {courseRow.key === deletionAttemptedCourse
                                                    ? <CircularProgress style={{height: 20, width: 20}}/>
                                                    : <Delete/>}
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>

                                    {/* Collapsible section */}
                                    <StyledTableRow>
                                        <StyledTableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={8}>
                                            <Collapse in={openIndex === index} timeout="auto" unmountOnExit>
                                                <Box margin={1}>
                                                    <CourseSummary courseKey={courseRow.key}/>
                                                </Box>
                                            </Collapse>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination section */}
            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={filteredCourses.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}

export default CoursesSection
